import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axiosInstance from '../../axiosConfig';

const fetchRawAttributes = async ({ id, params }) => {
  const response = await axiosInstance.get(
    `/mp_categories/${id}/raw_attributes/`,
    {
      params,
    }
  );
  return response.data;
};

export const useGetRawAttributes = (id, params = {}, options = {}) => {
  const queryParams = useMemo(() => {
    const out = {};
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    return out;
  }, [params]);

  const { data, error, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['raw-attributes', id, queryParams],
    queryFn: () => fetchRawAttributes({ id, params }),
    enabled: (options.enabled ?? true) && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
  });

  // Helper to extract name, label and add type info
  const extractWithType = (items, type, typeLabel) =>
    Array.isArray(items) && items.length > 0
      ? items.map(({ name, label, value_list, metric }) => ({
          name,
          label,
          type,
          typeLabel,
          value_list,
          metric,
        }))
      : [];

  const foreignKeyAttributes = [
    { attributeName: 'category', mappingName: 'category.name' },
    { attributeName: 'main_color', mappingName: 'main_color.name' },
    { attributeName: 'main_material', mappingName: 'main_material.name' },
  ];

  // Merge all attribute arrays into a single rawAttributes array
  const rawAttributesDatas = useMemo(() => {
    if (!data) return [];

    const structured = extractWithType(
      data.structured_attributes,
      'structured',
      'Attributs structurels'
    );
    const technical = extractWithType(
      data.technical_attributes,
      'technical',
      'Attributs techniques'
    );
    const marketing = extractWithType(
      data.marketing_attributes,
      'marketing',
      'Attribut marketing'
    );

    // Merge and remove duplicates based on name
    const merged = [...structured, ...technical, ...marketing];

    // Replace name with mappingName for foreign key attributes
    const mappedAttributes = merged.map((item) => {
      const foreignKey = foreignKeyAttributes.find(
        (fk) => fk.attributeName === item.name
      );
      if (foreignKey) {
        return { ...item, name: foreignKey.mappingName };
      }
      return item;
    });

    const seen = new Set();
    return mappedAttributes.filter((item) => {
      if (seen.has(item.name)) return false;
      seen.add(item.name);
      return true;
    });
  }, [data]);

  return {
    rawAttributesDatas,
    error,
    isLoading,
    isError,
    isFetching,
    refetch,
  };
};
